export type ReimbursementDocumentUploadStatusKey =
  | 'pending'
  | 'uploaded'
  | 'approved'
  | 'incomplete'
  | 'unknown'

const BADGE_BASE =
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap'

export function normalizeReimbursementUploadStatusKey(
  status: string,
): ReimbursementDocumentUploadStatusKey {
  const normalized = String(status ?? '').trim().toLowerCase()
  if (normalized === 'pending') return 'pending'
  if (normalized === 'uploaded') return 'uploaded'
  if (
    normalized === 'approved' ||
    normalized === 'received' ||
    normalized === 'complete' ||
    normalized === 'completed'
  ) {
    return 'approved'
  }
  if (
    normalized === 'incomplete' ||
    normalized === 'rejected' ||
    normalized === 'missing' ||
    normalized === 'denied'
  ) {
    return 'incomplete'
  }
  return 'unknown'
}

export function normalizeReimbursementUploadStatusLabel(status: string): string {
  const key = normalizeReimbursementUploadStatusKey(status)
  switch (key) {
    case 'pending':
      return 'Pending'
    case 'uploaded':
      return 'Uploaded'
    case 'approved':
      return 'Approved'
    case 'incomplete':
      return 'Incomplete'
    default:
      return String(status ?? '').trim() || 'Pending'
  }
}

export function getReimbursementDocumentUploadStatusBadgeClasses(status: string): string {
  const key = normalizeReimbursementUploadStatusKey(status)
  switch (key) {
    case 'pending':
      return `${BADGE_BASE} bg-gray-100 text-gray-700`
    case 'uploaded':
      return `${BADGE_BASE} bg-amber-100 text-amber-700`
    case 'approved':
      return `${BADGE_BASE} bg-green-100 text-green-700`
    case 'incomplete':
      return `${BADGE_BASE} bg-red-100 text-red-700`
    default:
      return `${BADGE_BASE} bg-gray-100 text-gray-700`
  }
}

export function getReimbursementDocumentUploadStatusTopBarClass(status: string): string {
  const key = normalizeReimbursementUploadStatusKey(status)
  switch (key) {
    case 'pending':
      return 'border-t-gray-300'
    case 'uploaded':
      return 'border-t-amber-400'
    case 'approved':
      return 'border-t-green-500'
    case 'incomplete':
      return 'border-t-red-500'
    default:
      return 'border-t-slate-200'
  }
}

const UPLOADED_FILE_CONTAINER_BASE = 'rounded-lg border'

/** Background + border for the uploaded-file row (matches status badge colors). */
export function getReimbursementDocumentUploadStatusContainerClasses(status: string): string {
  const key = normalizeReimbursementUploadStatusKey(status)
  switch (key) {
    case 'pending':
      return `${UPLOADED_FILE_CONTAINER_BASE} border-gray-300 bg-gray-50`
    case 'uploaded':
      return `${UPLOADED_FILE_CONTAINER_BASE} border-amber-300 bg-amber-50`
    case 'approved':
      return `${UPLOADED_FILE_CONTAINER_BASE} border-green-300 bg-green-50`
    case 'incomplete':
      return `${UPLOADED_FILE_CONTAINER_BASE} border-red-300 bg-red-50`
    default:
      return `${UPLOADED_FILE_CONTAINER_BASE} border-slate-300 bg-slate-50`
  }
}

export function getReimbursementDocumentUploadStatusAccentTextClass(status: string): string {
  const key = normalizeReimbursementUploadStatusKey(status)
  switch (key) {
    case 'uploaded':
      return 'text-amber-700'
    case 'approved':
      return 'text-green-700'
    case 'incomplete':
      return 'text-red-700'
    default:
      return 'text-slate-600'
  }
}

export function reimbursementDocumentUploadStatusShowsCheckIcon(status: string): boolean {
  return normalizeReimbursementUploadStatusKey(status) === 'approved'
}

export function reimbursementDocumentUploadStatusShowsXCircleIcon(status: string): boolean {
  return normalizeReimbursementUploadStatusKey(status) === 'incomplete'
}
